import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { NAVIGATION_SECTIONS } from '../../constants/accessControl';

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const { hasPermission } = useAuth();
  const location = useLocation();

  const navigationSections = useMemo(() => {
    return NAVIGATION_SECTIONS.map((section) => {
      const items = (section.items || []).filter((item) =>
        hasPermission(item.permission)
      );
      return { ...section, items };
    }).filter((section) => section.items.length > 0);
  }, [hasPermission]);

  const getActiveSectionId = () => {
    const active = navigationSections.find((section) =>
      section.items?.some((item) => location?.pathname === item?.path)
    );
    return active?.id || null;
  };

  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const activeId = getActiveSectionId();
    setExpandedSections((prev) => {
      const next = { ...prev };
      navigationSections.forEach((section) => {
        if (section.items?.length <= 1) return;
        if (next[section.id] === undefined) {
          next[section.id] = section.id === activeId;
        }
      });
      if (activeId) {
        next[activeId] = true;
      }
      return next;
    });
  }, [location?.pathname, navigationSections]);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev?.[sectionId],
    }));
  };

  const isActiveSection = (section) =>
    section?.items?.some((item) => location?.pathname === item?.path);

  const isActivePath = (path) => location?.pathname === path;

  const linkBase =
    'flex items-center gap-2 px-2.5 py-2 text-sm rounded-md transition-colors micro-interaction whitespace-nowrap';

  const activeLinkClass = 'bg-sidebar-active text-sidebar-foreground font-medium';
  const inactiveLinkClass =
    'text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-hover';

  return (
    <aside
      className={`fixed left-0 top-16 bottom-0 z-40 bg-sidebar border-r border-sidebar-border transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-72'
      } lg:translate-x-0`}
    >
      <div className="flex flex-col h-full">
        <div
          className={`flex items-center border-b border-sidebar-border ${
            isCollapsed ? 'justify-center p-3' : 'justify-between px-3 py-3'
          }`}
        >
          {!isCollapsed && (
            <h2 className="text-sm font-semibold text-sidebar-foreground whitespace-nowrap">
              Management
            </h2>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            iconName={isCollapsed ? 'ChevronRight' : 'ChevronLeft'}
            className="text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-hover shrink-0"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          />
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden p-2">
          <div className="space-y-1">
            {navigationSections?.map((section) => {
              const items = section.items || [];
              const hasMultiple = items.length > 1;
              const isExpanded = !!expandedSections?.[section?.id];

              return (
                <div key={section?.id} className="space-y-0.5">
                  {hasMultiple ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (isCollapsed) {
                          onToggle?.();
                          setExpandedSections((prev) => ({ ...prev, [section.id]: true }));
                          return;
                        }
                        toggleSection(section?.id);
                      }}
                      className={`w-full ${linkBase} ${
                        isCollapsed ? 'justify-center' : 'justify-between'
                      } ${
                        isActiveSection(section) ? activeLinkClass : inactiveLinkClass
                      }`}
                      title={section?.label}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Icon name={section?.icon} size={18} className="shrink-0" />
                        {!isCollapsed && (
                          <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                            {section?.label}
                          </span>
                        )}
                      </span>
                      {!isCollapsed && (
                        <Icon
                          name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                          size={16}
                          className="shrink-0"
                        />
                      )}
                    </button>
                  ) : (
                    items[0] && (
                      <Link
                        to={items[0].path}
                        className={`${linkBase} ${isCollapsed ? 'justify-center' : ''} ${
                          isActivePath(items[0].path) ? activeLinkClass : inactiveLinkClass
                        }`}
                        title={items[0].label}
                      >
                        <Icon name={items[0].icon || section.icon} size={18} className="shrink-0" />
                        {!isCollapsed && (
                          <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                            {items[0].label}
                          </span>
                        )}
                      </Link>
                    )
                  )}

                  {hasMultiple && isExpanded && !isCollapsed && (
                    <div className="ml-3 pl-2 border-l border-sidebar-border space-y-0.5">
                      {items.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`${linkBase} ${
                            isActivePath(item.path) ? activeLinkClass : inactiveLinkClass
                          }`}
                        >
                          <Icon name={item.icon} size={16} className="shrink-0" />
                          <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                            {item.label}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
